<script setup lang="ts">
import type { ImageListItem } from '#shared/types/entity'

import { PAGE_LABELS } from '#shared/constants/page-labels'
import { pageNumberFromQuery } from '#shared/lib/route'
import { computed } from 'vue'

import { useActiveCity } from '~/composables/useActiveCity'
import { usePageOverride } from '~/composables/useRepoApi'
import { useSimplePagePresentation } from '~/composables/useSimplePagePresentation'

const { data: casesData } = await useCases()
const hasCases = computed<boolean>(() => (casesData.value?.length ?? 0) > 0)

// Работы другого города не подставляются: без своих кейсов раздел показывает фото сервиса
// города (фото точек из CMS) без описаний работ, сроков и сумм; без фото — 404.
await useCities()
const activeCity = useActiveCity()
const cityId = computed<string | undefined>(() => activeCity.value?.id)
const { data: locationsData } = await useLocations(cityId)
const branchImages = computed<ImageListItem[]>(() =>
  (locationsData.value ?? []).flatMap((location) => location.images ?? [])
)
const branchAddress = computed<string | undefined>(() => locationsData.value?.[0]?.address)

if (!hasCases.value && branchImages.value.length === 0) {
  throw createError({ statusCode: 404, statusMessage: 'Cases not found' })
}
const route = useRoute()
const casesPerPage = 9

const totalPages = computed<number>(() => {
  const itemsCount = casesData.value?.length ?? 0
  return Math.max(1, Math.ceil(itemsCount / casesPerPage))
})

// Страницы списка за пределами диапазона нет (404); у фото сервиса страница одна.
if ((pageNumberFromQuery(route.query) ?? 1) > totalPages.value) {
  throw createError({ statusCode: 404, statusMessage: 'Page not found' })
}

const currentPage = computed<number>(() => {
  const rawValue = Array.isArray(route.query.page) ? route.query.page[0] : route.query.page
  const parsedValue = Number.parseInt(String(rawValue ?? '1'), 10)

  if (!Number.isFinite(parsedValue) || parsedValue < 1) {
    return 1
  }

  return Math.min(parsedValue, totalPages.value)
})

const caseCards = computed<CaseItem[]>(() => {
  const items = casesData.value ?? []
  const startIndex = (currentPage.value - 1) * casesPerPage

  return items.slice(startIndex, startIndex + casesPerPage)
})

const cityLocative = computed<string>(() => activeCity.value?.name_locative?.trim() ?? '')
const pageTitle = computed<string>(() =>
  hasCases.value ? PAGE_LABELS.cases : `Фото сервиса ${cityLocative.value}`.trim()
)
const pageDescription = computed<string>(() =>
  hasCases.value
    ? 'Реальные кейсы по ремонту АКПП: симптомы, выполненные работы, стоимость ремонта и результат по конкретным автомобилям.'
    : `Фото сервиса «АКПП-Центр» ${cityLocative.value}.`
)

const { data: pageOverride } = await usePageOverride('work')

useSimplePagePresentation({
  pageOverride,
  title: pageTitle,
  description: pageDescription,
  baseItems: computed(() => [{ name: 'Главная', slug: '/' }]),
})
</script>

<template>
  <Breadcrumbs />
  <Section v-if="!hasCases">
    <BranchGallery :images="branchImages" :address="branchAddress" />
  </Section>
  <Section v-else>
    <div class="grid gap-12 sm:grid-cols-2 lg:grid-cols-3">
      <CaseGridCard v-for="caseItem in caseCards" :key="caseItem.id" :caseItem />
    </div>

    <AppPagination base-path="/work" :current-page="currentPage" :total-pages="totalPages" />
  </Section>
</template>
